import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useTranslation } from 'react-i18next';
import { WindowData } from '@/util/bcg/types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface VitalSignsChartProps {
  windows: WindowData[];
  type: 'hr' | 'rmssd' | 'resp' | 'dqi';
  className?: string;
}

const VitalSignsChart: React.FC<VitalSignsChartProps> = ({ 
  windows, 
  type, 
  className = '' 
}) => {
  const { t, i18n } = useTranslation();
  
  const getChartConfig = (type: string) => {
    switch (type) {
      case 'hr':
        return {
          title: t('mentalHealth.detail.heartRateChart'),
          yLabel: 'BPM',
          color: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgba(239, 68, 68, 1)',
          dataKey: 'HR_bpm' as keyof WindowData,
        };
      case 'rmssd':
        return {
          title: 'RMSSD',
          yLabel: 'ms',
          color: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgba(34, 197, 94, 1)',
          dataKey: 'RMSSD_ms' as keyof WindowData,
        };
      case 'resp':
        return {
          title: t('mentalHealth.detail.respirationChart'),
          yLabel: 'BPM',
          color: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          dataKey: 'Resp_bpm' as keyof WindowData,
        };
      case 'dqi':
        return {
          title: t('mentalHealth.detail.dqiChart'),
          yLabel: 'Quality (0-1)',
          color: 'rgba(168, 85, 247, 0.8)',
          borderColor: 'rgba(168, 85, 247, 1)',
          dataKey: 'DQI' as keyof WindowData,
        };
      default:
        return {
          title: t('common.data'),
          yLabel: 'Value',
          color: 'rgba(107, 114, 128, 0.8)',
          borderColor: 'rgba(107, 114, 128, 1)',
          dataKey: 'HR_bpm' as keyof WindowData,
        };
    }
  };

  const config = getChartConfig(type);
  
  // 유효한 데이터만 필터링
  const validWindows = windows.filter(w => {
    const value = w[config.dataKey] as number;
    return isFinite(value) && (type !== 'dqi' || w.DQI >= 0.3); // DQI는 최소 0.3 이상만 표시
  });

  const chartData = {
    labels: validWindows.map(w => w.t_start),
    datasets: [
      {
        label: config.title,
        data: validWindows.map(w => w[config.dataKey] as number),
        backgroundColor: config.color,
        borderColor: config.borderColor,
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: config.title,
        font: {
          size: 14,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const date = new Date(context[0].label);
            return date.toLocaleString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR');
          },
          label: (context: any) => {
            return `${config.title}: ${context.parsed.y.toFixed(2)} ${config.yLabel}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        title: {
          display: true,
          text: t('mentalHealth.detail.timeLabel'),
        },
      },
      y: {
        beginAtZero: type === 'dqi',
        title: {
          display: true,
          text: config.yLabel,
        },
        ...(type === 'dqi' && {
          min: 0,
          max: 1,
        }),
      },
    },
  };

  if (validWindows.length === 0) {
    return (
      <div className={`p-6 bg-gray-50 rounded-lg border ${className}`}>
        <div className="text-center text-gray-500">
          <div className="text-lg font-medium mb-2">{config.title}</div>
          <div className="text-sm">{t('common.noData')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 bg-white rounded-lg border ${className}`}>
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
      
      {/* 통계 정보 */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <div className="text-gray-600">{t('mentalHealth.detail.average')}</div>
          <div className="font-medium">
            {(validWindows.reduce((sum, w) => sum + (w[config.dataKey] as number), 0) / validWindows.length).toFixed(2)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-gray-600">{t('mentalHealth.detail.minimum')}</div>
          <div className="font-medium">
            {Math.min(...validWindows.map(w => w[config.dataKey] as number)).toFixed(2)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-gray-600">{t('mentalHealth.detail.maximum')}</div>
          <div className="font-medium">
            {Math.max(...validWindows.map(w => w[config.dataKey] as number)).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VitalSignsChart;

